import React from 'react';

import classNames from 'classnames';

import { hydrationHooks } from '../../hooks/hydrationHooks/hydrationHooks';
import { Typography } from '../Typography/Typography';
import { InputWarning } from '../icons';
import { InputWarningColor } from '../icons/InputWarning/InputWarning';
import styles from './Input.module.css';

export enum InputType {
  Input = 'input',
  TextArea = 'textarea',
}

export enum InputState {
  Disabled = 'disabled',
  Error = 'error',
}

export type BaseProps = {
  autoComplete?: string;
  className?: string;
  dataTau?: string;
  endAdornment?: JSX.Element;

  endAdornmentClassName?: string;
  errorMessage?: string | JSX.Element | null;

  infoMessage?: string | JSX.Element;
  inputAdditionalClass?: string;
  inputClassName?: string;

  inputInnerWrapperClassname?: string;

  inputMode?: FormInputMode;
  label?: string | JSX.Element;
  labelClassName?: string;
  labelHelper?: JSX.Element;

  labelSize?: 'small' | 'default';
  maxWidth?: number | 'unset';
  messageWrapperClassName?:string;
  placeholder?: string;
  startAdornment?: JSX.Element;
  startAdornmentClassName?: string;
  state?: InputState;
  textSecurity?: InputTextSecurity;
};

export type BaseFormInputProps = Omit<
  BaseProps,
  'label'
  | 'state'
  | 'infoMessage'
  | 'errorMessage'
>;

export enum FormInputMode {
  decimal = 'decimal',
  email = 'email',
  none = 'none',
  numeric = 'numeric',
  search = 'search',
  tel = 'tel',
  text = 'text',
  url = 'url',
}

export enum InputTextSecurity {
  circle = 'circle',
  disc = 'disc',
  none = 'none',
  square = 'square',
}

export type InputProps =
  BaseProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'inputMode'>
  & {
    inputType: InputType.Input;
    onChange?: ((value: React.ChangeEvent<HTMLInputElement>) => void);
    prefixLabel?: React.ReactNode;
    ref?: React.ForwardedRef<HTMLInputElement>;
  };

export type TextAreaProps =
  BaseProps &
  Omit<React.InputHTMLAttributes<HTMLTextAreaElement | HTMLInputElement>, 'inputMode'>
  & {
    amountOfCharactersAllowed?: number;
    inputType: InputType.TextArea;
    onChange?: ((value: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void);
    prefixLabel?: React.ReactNode;
    ref?: React.ForwardedRef<HTMLTextAreaElement | HTMLInputElement>;
  };

export type Props = InputProps | TextAreaProps;

export const Input = React.memo(React.forwardRef((
  props: Props,
  ref: React.ForwardedRef<HTMLInputElement | HTMLTextAreaElement>,
) => {
  const {
    className,
    labelClassName,
    inputClassName,

    maxWidth = 'unset',
    placeholder,
    prefixLabel: prefix,

    label,
    labelSize = 'default',
    labelHelper,

    state,
    infoMessage,
    errorMessage,

    startAdornment,
    endAdornment,

    startAdornmentClassName,
    endAdornmentClassName,

    textSecurity,
    dataTau,
    inputAdditionalClass,
    messageWrapperClassName,
    inputInnerWrapperClassname,
    ...rest
  } = props;

  // so that inputType isn't passed as a prop to the input or textarea
  const { inputType, ...inputProps } = rest;

  let textAreaProps = null;
  let textareaMaxLengthExceeded = false;
  let textAreaWithMaxLength = null;
  const valueLengthOrStub = typeof rest.value === 'string' ? rest.value.length : 0;
  if (rest.inputType === InputType.TextArea) {
    const { amountOfCharactersAllowed, inputType: _, ...textAreaRest } = rest;
    textAreaProps = textAreaRest;
    textAreaWithMaxLength = amountOfCharactersAllowed;
    textareaMaxLengthExceeded =
      !!amountOfCharactersAllowed && valueLengthOrStub > amountOfCharactersAllowed;
  }

  const wrapperClass = classNames(
    styles.inputWrapper,
    className,
    state ? styles[state] : undefined,
    !state && textareaMaxLengthExceeded ? styles.error : undefined,
  );

  const wrapperStyle = maxWidth !== 'unset' ? { maxWidth: `${maxWidth}px` } : {};

  const inputClass = classNames(
    styles.input,
    inputType === InputType.TextArea ? styles.textArea : undefined,
    startAdornment ? styles.inputWithLeftIcon : undefined,
    endAdornment || textAreaWithMaxLength
      ? styles.inputWithRightIcon : undefined,
    textAreaWithMaxLength ? styles.textAreaWithMaxLength : undefined,
    inputClassName,
  );

  const { isClient } = hydrationHooks.useIsClient();

  return (
    <>
      {label ? (
        <div className={styles.labelContainer}>
          <Typography
            component='label'
            variant={labelSize === 'small' ? 'bodySubText2' : 'body2'}
            size={labelSize === 'small' ? 'l' : undefined}
            className={classNames(styles.inputLabel, labelClassName)}
          >
            {label}
          </Typography>

          {labelHelper
            && <div
              className={classNames(styles.inputLabel,
                { [styles.noWrap]: CONFIG.isHyphenation })}
            >
              {labelHelper}
            </div>}
        </div>
      ) : null}

      <div className={wrapperClass} style={wrapperStyle}>
        <div className={classNames(
          styles.inputInnerWrapper,
          prefix ? styles.inputInnerWrapperWithPrefix : undefined,
          prefix ? inputClass : undefined,
          inputInnerWrapperClassname,
        )}
        >
          {startAdornment && (
            <div className={classNames(styles.startAdornment, startAdornmentClassName)}>
              {startAdornment}
            </div>
          )}

          {prefix && (
            <div
              className={styles.prefix}
            >{prefix}</div>
          )}

          {inputType === InputType.Input ? (
            <input
              {...(inputProps as InputProps)}
              type={inputProps?.type || 'text'}
              className={classNames(
                inputClass,
                prefix ? styles.inputWithPrefix : undefined,
                inputAdditionalClass,
              )}
              placeholder={placeholder}
              readOnly={state === 'disabled' || !isClient}
              onChange={props.onChange as React.ChangeEventHandler<HTMLInputElement>}
              ref={ref as React.ForwardedRef<HTMLInputElement>}
              style={{ '--webkit-text-security': textSecurity ?? 'unset' } as React.CSSProperties}
              data-tau={dataTau}
            />
          ) : (
            <textarea
              {...(textAreaProps as TextAreaProps)}
              className={inputClass}
              placeholder={placeholder}
              disabled={state === 'disabled' || !isClient}
              onChange={props.onChange as React.ChangeEventHandler<HTMLTextAreaElement>}
              ref={ref as React.ForwardedRef<HTMLTextAreaElement>}
              style={{ '--webkit-text-security': textSecurity ?? 'unset' } as React.CSSProperties}
            />
          )}

          {endAdornment && (
            <div className={classNames(styles.endAdornment, endAdornmentClassName)}>
              {endAdornment}
            </div>
          )}

          {rest.inputType === InputType.TextArea && rest.amountOfCharactersAllowed && (
            <Typography
              size='l'
              variant='bodySubText1'
              className={styles.amountOfCharactersAllowed}
            >
              {rest.amountOfCharactersAllowed - valueLengthOrStub}
            </Typography>
          )}
        </div>

        {state === 'error'
          ? errorMessage && (
            <div className={classNames(styles.messageWrapper, messageWrapperClassName)}>
              <InputWarning />

              <Typography size='l' variant='bodySubText1' className={styles.message}>
                {errorMessage}
              </Typography>
            </div>
          )
          : infoMessage && (
            <div className={classNames(styles.messageWrapper, messageWrapperClassName)}>
              <InputWarning
                color={InputWarningColor.secondary}
                reversedExclamationMark
              />

              <Typography size='l' variant='bodySubText1' className={styles.message}>
                {infoMessage}
              </Typography>
            </div>
          )}
      </div>
    </>
  );
}));

Input.displayName = 'Input';
